/**
 * CrossFeedbackPanel.jsx - Sistema B6 Feedback Entre Grupos
 * 
 * Permite a grupos evaluar y dar feedback a otros grupos de la misma sección:
 * - Vista de entregables de otros grupos de la semana actual
 * - Sistema de evaluación estructurada entre pares (5 criterios pedagógicos)
 * - Comentarios constructivos y específicos por grupo evaluador
 * - Matriz de feedback bidireccional (quién evaluó a quién)
 * - Consolidación de feedback recibido para reflexión grupal
 * 
 * Flujo: Ver Grupos → Evaluar Entregables → Dar Feedback → Ver Feedback Recibido
 * Solo accesible después de haber completado el entregable propio de la semana
 * 
 * @component
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft,
  Users, 
  MessageSquare,
  Star,
  Eye,
  Send,
  CheckCircle,
  Clock,
  AlertCircle,
  Target,
  Lightbulb,
  ThumbsUp,
  ThumbsDown,
  BarChart3,
  FileText,
  User,
  Calendar,
  Award,
  TrendingUp,
  Filter,
  RefreshCw,
  Download,
  ExternalLink
} from 'lucide-react';
import apiClient from '../services/apiClient';

const CrossFeedbackPanel = () => {
  const { user } = useAuth();
  const { sectionId, groupId, week } = useParams();
  const navigate = useNavigate();
  
  // Estados principales del componente
  const [section, setSection] = useState(null);
  const [currentGroup, setCurrentGroup] = useState(null);
  const [otherGroups, setOtherGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Estados del sistema de evaluación
  const [selectedGroupForEvaluation, setSelectedGroupForEvaluation] = useState(null);
  const [evaluationCriteria] = useState([
    { id: 'clarity', name: 'Claridad y Estructura', weight: 20 },
    { id: 'innovation', name: 'Innovación y Creatividad', weight: 25 },
    { id: 'feasibility', name: 'Viabilidad y Realismo', weight: 20 },
    { id: 'collaboration', name: 'Evidencia de Colaboración', weight: 15 },
    { id: 'depth', name: 'Profundidad de Análisis', weight: 20 }
  ]);
  const [currentEvaluation, setCurrentEvaluation] = useState({});
  const [evaluationComment, setEvaluationComment] = useState('');
  const [isSubmittingEvaluation, setIsSubmittingEvaluation] = useState(false);

  // Estados de feedback envido y recibido
  const [sentFeedback, setSentFeedback] = useState([]); // Feedback que mi grupo ha enviado
  const [receivedFeedback, setReceivedFeedback] = useState([]); // Feedback que hemos recibido
  const [feedbackMatrix, setFeedbackMatrix] = useState({}); // Matriz completa de feedback

  // Estados de filtros y vista
  const [activeTab, setActiveTab] = useState('evaluate'); // 'evaluate', 'sent', 'received', 'matrix'
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'pending', 'completed'
  const [showDeliverablePreview, setShowDeliverablePreview] = useState(null);

  // Permisos y validaciones
  const [ownDeliverableCompleted, setOwnDeliverableCompleted] = useState(false);
  const [canEvaluateGroups, setCanEvaluateGroups] = useState(false);

  /**
   * Cargar datos iniciales
   */
  const loadInitialData = useCallback(async () => {
    setLoading(true);
    try {
      // Cargar información de la sección y grupo actual
      const [sectionRes, groupRes] = await Promise.all([
        apiClient.get(`/api/sections/${sectionId}`),
        apiClient.get(`/api/groups/${groupId}`)
      ]);

      setSection(sectionRes.data);
      setCurrentGroup(groupRes.data);

      // Verificar si el grupo actual completó su entregable
      const ownDeliverable = await apiClient.get(`/api/weekly-deliverables/${groupId}/${week}/status`);
      setOwnDeliverableCompleted(ownDeliverable.data.completed);
      setCanEvaluateGroups(ownDeliverable.data.completed);

      if (ownDeliverable.data.completed) {
        // Cargar otros grupos de la sección (excluyendo el propio)
        const otherGroupsRes = await apiClient.get(`/api/sections/${sectionId}/groups/others/${groupId}`);
        setOtherGroups(otherGroupsRes.data.filter(g => g.groupId !== groupId));

        // Cargar matriz de feedback para la semana
        const feedbackMatrixRes = await apiClient.get(`/api/cross-feedback/${sectionId}/${week}/matrix`);
        setFeedbackMatrix(feedbackMatrixRes.data);

        // Cargar feedback enviado y recibido
        const [sentRes, receivedRes] = await Promise.all([
          apiClient.get(`/api/cross-feedback/${groupId}/${week}/sent`),
          apiClient.get(`/api/cross-feedback/${groupId}/${week}/received`)
        ]);
        
        setSentFeedback(sentRes.data);
        setReceivedFeedback(receivedRes.data);
      }

    } catch (error) {
      console.error('Error cargando datos de feedback cruzado:', error);
      setError('Error cargando el sistema de feedback entre grupos');
    } finally {
      setLoading(false);
    }
  }, [sectionId, groupId, week]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  /**
   * Cargar entregable de un grupo para evaluación
   */
  const loadGroupDeliverable = async (targetGroupId) => {
    try {
      const response = await apiClient.get(`/api/weekly-deliverables/${targetGroupId}/${week}/view`);
      setSelectedGroupForEvaluation({
        ...otherGroups.find(g => g.groupId === targetGroupId),
        deliverable: response.data
      });
      // Limpiar evaluación anterior
      setCurrentEvaluation({});
      setEvaluationComment('');
    } catch (error) {
      setError('Error cargando el entregable del grupo');
    }
  };

  /**
   * Actualizar puntuación de un criterio
   */
  const updateCriteriaScore = (criteriaId, score) => {
    setCurrentEvaluation(prev => ({
      ...prev,
      [criteriaId]: Math.max(1, Math.min(10, parseInt(score) || 0))
    }));
  };

  /**
   * Calcular puntuación total ponderada
   */
  const calculateTotalScore = () => {
    let totalScore = 0;
    let totalWeight = 0;
    
    evaluationCriteria.forEach(criteria => {
      if (currentEvaluation[criteria.id]) {
        totalScore += (currentEvaluation[criteria.id] * criteria.weight);
        totalWeight += criteria.weight;
      }
    });

    return totalWeight > 0 ? (totalScore / totalWeight).toFixed(1) : 0;
  };

  /**
   * Enviar evaluación de feedback
   */
  const submitEvaluation = async () => {
    if (!selectedGroupForEvaluation) return;

    // Validar que todos los criterios estén evaluados
    const missingCriteria = evaluationCriteria.filter(c => !currentEvaluation[c.id]);
    if (missingCriteria.length > 0) {
      setError(`Complete la evaluación de: ${missingCriteria.map(c => c.name).join(', ')}`);
      return;
    }

    if (!evaluationComment.trim()) {
      setError('Debe incluir un comentario constructivo');
      return;
    }

    setIsSubmittingEvaluation(true);
    try {
      const feedbackData = {
        evaluatorGroupId: groupId,
        evaluatedGroupId: selectedGroupForEvaluation.groupId,
        week: parseInt(week),
        sectionId,
        criteria: evaluationCriteria.map(c => ({
          criteriaId: c.id,
          name: c.name,
          score: currentEvaluation[c.id],
          weight: c.weight
        })),
        totalScore: parseFloat(calculateTotalScore()),
        comment: evaluationComment.trim(),
        evaluatedBy: {
          userId: user.id,
          userName: user.nombre,
          groupName: currentGroup?.groupName || 'Grupo'
        },
        submittedAt: new Date().toISOString()
      };

      await apiClient.post('/api/cross-feedback/submit', feedbackData);
      
      setSuccess(`Feedback enviado al ${selectedGroupForEvaluation.groupName}`);
      
      // Actualizar estado local
      setSentFeedback(prev => [...prev, feedbackData]);
      setSelectedGroupForEvaluation(null);
      
      // Recargar datos
      await loadInitialData();
      
    } catch (error) {
      setError('Error enviando el feedback');
      console.error('Error:', error);
    } finally {
      setIsSubmittingEvaluation(false);
    }
  };

  /**
   * Verificar si ya evaluamos un grupo específico
   */
  const hasEvaluatedGroup = (targetGroupId) => {
    return sentFeedback.some(f => f.evaluatedGroupId === targetGroupId);
  };

  /**
   * Obtener estadísticas de feedback recibido
   */
  const getReceivedFeedbackStats = () => {
    if (receivedFeedback.length === 0) return null;

    const scores = receivedFeedback.map(f => f.totalScore);
    const avgScore = scores.reduce((a, b) => a + b, 0) / scores.length;
    
    const criteriaStats = {};
    evaluationCriteria.forEach(criteria => {
      const criteriaScores = receivedFeedback
        .flatMap(f => f.criteria.filter(c => c.criteriaId === criteria.id))
        .map(c => c.score);
      
      if (criteriaScores.length > 0) {
        criteriaStats[criteria.id] = {
          name: criteria.name,
          avg: criteriaScores.reduce((a, b) => a + b, 0) / criteriaScores.length,
          count: criteriaScores.length
        };
      }
    });

    return {
      totalFeedbacks: receivedFeedback.length,
      averageScore: avgScore,
      criteriaStats
    };
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Cargando sistema de feedback entre grupos...</p>
        </div>
      </div>
    );
  }

  // Error state o requisitos no cumplidos
  if (!ownDeliverableCompleted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Completa tu entregable primero</h2>
          <p className="text-gray-600 mb-6">
            Para evaluar otros grupos, primero debes completar y enviar el entregable de tu propio grupo para la semana {week}.
          </p>
          <div className="space-y-3">
            <button 
              onClick={() => navigate(`/teaching/sections/${sectionId}/groups/${groupId}/weekly/${week}`)}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700"
            >
              Completar Entregable
            </button>
            <button 
              onClick={() => navigate(`/teaching/sections/${sectionId}/groups`)}
              className="w-full text-gray-600 py-2 px-4 rounded-lg border border-gray-300 hover:bg-gray-50"
            >
              Volver a Grupos
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (error && !section) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-4" />
          <p className="text-red-600">{error}</p>
          <button 
            onClick={() => navigate(`/teaching/sections/${sectionId}/groups`)}
            className="mt-4 text-blue-600 hover:text-blue-700"
          >
            Volver a grupos
          </button>
        </div>
      </div>
    );
  }

  const stats = getReceivedFeedbackStats();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(`/teaching/sections/${sectionId}/groups`)}
                className="text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Feedback Entre Grupos - Semana {week}
                </h1>
                <p className="text-sm text-gray-600">
                  {section?.title} • {currentGroup?.groupName}
                </p>
              </div>
            </div>
            
            {/* Estadísticas rápidas */}
            <div className="flex items-center space-x-6 text-sm">
              <div className="text-center">
                <div className="text-lg font-bold text-blue-600">{sentFeedback.length}</div>
                <div className="text-gray-500">Enviados</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-green-600">{receivedFeedback.length}</div>
                <div className="text-gray-500">Recibidos</div>
              </div>
              {stats && (
                <div className="text-center">
                  <div className="text-lg font-bold text-purple-600">{stats.averageScore.toFixed(1)}</div>
                  <div className="text-gray-500">Promedio</div>
                </div>
              )}
            </div>
          </div>

          {/* Navegación por pestañas */}
          <div className="mt-4">
            <nav className="flex space-x-8">
              {[
                { id: 'evaluate', label: 'Evaluar Grupos', icon: Target },
                { id: 'sent', label: 'Feedback Enviado', icon: Send },
                { id: 'received', label: 'Feedback Recibido', icon: MessageSquare },
                { id: 'matrix', label: 'Matriz General', icon: BarChart3 }
              ].map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                      activeTab === tab.id
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
      </div>

      {/* Contenido principal */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Alertas */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
            {success}
          </div>
        )}

        {/* Tab: Evaluar Grupos */}
        {activeTab === 'evaluate' && (
          <div className="space-y-6">
            {!selectedGroupForEvaluation ? (
              /* Lista de grupos disponibles para evaluar */
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Grupos Disponibles para Evaluar
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {otherGroups.map(group => {
                    const alreadyEvaluated = hasEvaluatedGroup(group.groupId);
                    return (
                      <div
                        key={group.groupId}
                        className={`bg-white rounded-lg border p-4 ${
                          alreadyEvaluated 
                            ? 'border-green-200 bg-green-50' 
                            : 'border-gray-200 hover:border-blue-300 hover:shadow-md cursor-pointer'
                        } transition-all`}
                        onClick={() => !alreadyEvaluated && loadGroupDeliverable(group.groupId)}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="font-semibold text-gray-900">
                              {group.groupName}
                            </h3>
                            <p className="text-sm text-gray-600">
                              {group.members.length} miembros
                            </p>
                          </div>
                          {alreadyEvaluated ? (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          ) : (
                            <Target className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                        
                        <div className="text-xs text-gray-500 mb-3">
                          {group.members.map(m => m.userName).join(', ')}
                        </div>
                        
                        <div className="flex justify-between items-center">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            alreadyEvaluated
                              ? 'bg-green-100 text-green-700'
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {alreadyEvaluated ? 'Evaluado' : 'Pendiente'}
                          </span>
                          {!alreadyEvaluated && (
                            <Eye className="w-4 h-4 text-gray-400" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {otherGroups.length === 0 && (
                  <div className="text-center py-8">
                    <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No hay otros grupos en esta sección para evaluar</p>
                  </div>
                )}
              </div>
            ) : (
              /* Panel de evaluación del grupo seleccionado */
              <div className="space-y-6">
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900">
                      Evaluando: {selectedGroupForEvaluation.groupName}
                    </h2>
                    <button
                      onClick={() => setSelectedGroupForEvaluation(null)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <ArrowLeft className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Entregable del grupo a evaluar */}
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-medium text-gray-900 mb-2">Entregable de la Semana {week}</h3>
                    <div className="text-sm text-gray-700 whitespace-pre-wrap max-h-64 overflow-y-auto">
                      {selectedGroupForEvaluation.deliverable?.content || 'No hay contenido disponible'}
                    </div>
                  </div>

                  {/* Criterios de evaluación */}
                  <div className="space-y-4 mb-6">
                    <h3 className="font-medium text-gray-900">Criterios de Evaluación</h3>
                    {evaluationCriteria.map(criteria => (
                      <div key={criteria.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-gray-900">{criteria.name}</span>
                          <span className="text-xs text-gray-500">Peso: {criteria.weight}%</span>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center space-x-2">
                            {[1,2,3,4,5,6,7,8,9,10].map(score => (
                              <button
                                key={score}
                                onClick={() => updateCriteriaScore(criteria.id, score)}
                                className={`w-8 h-8 text-xs rounded ${
                                  currentEvaluation[criteria.id] === score
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                              >
                                {score}
                              </button>
                            ))}
                          </div>
                          {currentEvaluation[criteria.id] && (
                            <span className="text-sm font-medium text-blue-600">
                              {currentEvaluation[criteria.id]}/10
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Puntuación total */}
                  <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">
                        {calculateTotalScore()}/10
                      </div>
                      <div className="text-sm text-blue-700">Puntuación Total Ponderada</div>
                    </div>
                  </div>

                  {/* Comentario constructivo */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Comentario Constructivo (Obligatorio)
                    </label>
                    <textarea
                      value={evaluationComment}
                      onChange={(e) => setEvaluationComment(e.target.value)}
                      rows={4}
                      className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Proporciona feedback constructivo específico sobre fortalezas y áreas de mejora..."
                    />
                    <div className="text-xs text-gray-500 mt-1">
                      {evaluationComment.length} caracteres
                    </div>
                  </div>

                  {/* Botón de envío */}
                  <button
                    onClick={submitEvaluation}
                    disabled={isSubmittingEvaluation || calculateTotalScore() === '0' || !evaluationComment.trim()}
                    className="w-full bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {isSubmittingEvaluation ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                        Enviando Evaluación...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Enviar Evaluación
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tab: Feedback Enviado */}
        {activeTab === 'sent' && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Feedback Enviado por tu Grupo
            </h2>
            <div className="space-y-4">
              {sentFeedback.map((feedback, index) => (
                <div key={index} className="bg-white rounded-lg border border-gray-200 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-900">
                      Evaluación a {otherGroups.find(g => g.groupId === feedback.evaluatedGroupId)?.groupName}
                    </h3>
                    <div className="flex items-center space-x-2 text-sm text-gray-500">
                      <Calendar className="w-4 h-4" />
                      {new Date(feedback.submittedAt).toLocaleDateString()}
                    </div>
                  </div>
                  
                  <div className="mb-3 p-3 bg-green-50 rounded-lg">
                    <div className="text-center">
                      <div className="text-xl font-bold text-green-600">
                        {feedback.totalScore}/10
                      </div>
                      <div className="text-sm text-green-700">Puntuación Otorgada</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                    {feedback.criteria.map(criteria => (
                      <div key={criteria.criteriaId} className="text-sm">
                        <span className="font-medium">{criteria.name}:</span> 
                        <span className="ml-2 text-blue-600">{criteria.score}/10</span>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-gray-100 pt-3">
                    <p className="text-sm text-gray-700">{feedback.comment}</p>
                  </div>
                </div>
              ))}
              
              {sentFeedback.length === 0 && (
                <div className="text-center py-8">
                  <Send className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Aún no has enviado ningún feedback</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tab: Feedback Recibido */}
        {activeTab === 'received' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                Feedback Recibido
              </h2>
              {stats && (
                <div className="bg-purple-50 px-4 py-2 rounded-lg">
                  <span className="text-sm text-purple-700">
                    Promedio General: <span className="font-bold">{stats.averageScore.toFixed(1)}/10</span>
                    <span className="ml-2">({stats.totalFeedbacks} evaluaciones)</span>
                  </span>
                </div>
              )}
            </div>

            {/* Estadísticas por criterio */}
            {stats && stats.criteriaStats && (
              <div className="mb-6 bg-white rounded-lg border border-gray-200 p-4">
                <h3 className="font-medium text-gray-900 mb-3">Análisis por Criterios</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.values(stats.criteriaStats).map(criteria => (
                    <div key={criteria.name} className="text-center">
                      <div className="text-lg font-bold text-blue-600">
                        {criteria.avg.toFixed(1)}/10
                      </div>
                      <div className="text-sm text-gray-700 font-medium">{criteria.name}</div>
                      <div className="text-xs text-gray-500">({criteria.count} evaluaciones)</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-4">
              {receivedFeedback.map((feedback, index) => (
                <div key={index} className="bg-white rounded-lg border border-gray-200 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-900">
                      Evaluación de {feedback.evaluatedBy.groupName}
                    </h3>
                    <div className="flex items-center space-x-2 text-sm text-gray-500">
                      <User className="w-4 h-4" />
                      {feedback.evaluatedBy.userName}
                      <Calendar className="w-4 h-4 ml-2" />
                      {new Date(feedback.submittedAt).toLocaleDateString()}
                    </div>
                  </div>
                  
                  <div className="mb-3 p-3 bg-blue-50 rounded-lg">
                    <div className="text-center">
                      <div className="text-xl font-bold text-blue-600">
                        {feedback.totalScore}/10
                      </div>
                      <div className="text-sm text-blue-700">Puntuación Recibida</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                    {feedback.criteria.map(criteria => (
                      <div key={criteria.criteriaId} className="text-sm">
                        <span className="font-medium">{criteria.name}:</span> 
                        <span className="ml-2 text-blue-600">{criteria.score}/10</span>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-gray-100 pt-3">
                    <p className="text-sm text-gray-700">{feedback.comment}</p>
                  </div>
                </div>
              ))}
              
              {receivedFeedback.length === 0 && (
                <div className="text-center py-8">
                  <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Aún no has recibido ningún feedback</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tab: Matriz General */}
        {activeTab === 'matrix' && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Matriz de Feedback de la Sección
            </h2>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <p className="text-gray-600 text-center py-8">
                Vista de matriz general de feedback entre todos los grupos
                <br />
                <span className="text-sm">(Funcionalidad en desarrollo)</span>
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CrossFeedbackPanel;